import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Box,
  Backdrop,
  CircularProgress,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Compra, FuncionConDate, IFichaEspectaculo, UsuarioDTO } from 'utils/models/types';
import {
  formatearFuncionesConDate,
  formatoFechaHora,
  getCompraConExpiracion,
  storeDatosConExpiracion,
} from 'utils/utilities';
import CarouselFunciones from './CarouselFunciones';
import { useForm } from 'react-hook-form';
import InformacionEspectaculo from './InformacionEspectaculo';
import { theme } from 'theme/theme';
import CaratulaObra from './CaratulaObra';
import PasosDeCompra from './PasosDeCompra';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'utils/redux/store/store';
import Spinner from 'components/public/Spinner/Spinner';
import { alturaContainer, anchoMaxForms } from 'utils/constants';
import { fetchFichaEspectaculoPorId } from 'utils/redux/features/espectaculoFichaSlice';
import RespuestaServidor from 'pages/auxiliares/RespuestaServidor';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ChevronLeft } from '@mui/icons-material';
import NoImage from 'assets/afichesObras/No-image.jpg';
import GaleriaEspectaculo from '../GaleriaEspectaculo/GaleriaEspectaculo';
import PageContainer from 'components/PageContainer/PageContainer';
import { fetchFuncionesEspectaculo } from '../../../utils/redux/features/funcionesPorIdEspectaculoSlice';
import { useError } from 'services/ErrorContext';
import { getAuthToken } from 'services/apiUtils';
import { loginFailure } from 'utils/redux/features/authSlice';
import ModalConfirmacion from '../Modal/ModalConfirmacion';
import { compraEntrada } from 'services/servicioPost';
import { clearErrorCompra } from 'utils/storage/compra';

const PAYMENT_FLOW_MODE_KEY = 'paymentFlowMode';

const FichaEspectaculo = () => {
  const { id } = useParams();
  const usuarioJSON = localStorage.getItem('usuario');
  const usuario: UsuarioDTO = usuarioJSON && JSON.parse(usuarioJSON);
  const esAdmin = usuario?.Rol === 'Administrador';
  const [cantidadAsientos, setCantidadAsientos] = useState(0);
  const [openModalConfirmacion, setOpenModalConfirmacion] = useState(false);
  const [openSpinner, setOpenSpinner] = useState(false);
  const [funcionSeleccionada, setFuncionSeleccionada] = useState<FuncionConDate | undefined | null>(null);
  const [imgSrc, setImgSrc] = useState<string>('');
  const [tipoCompra, setTipoCompra] = useState<number>(1);
  const { setError, clearError, setLoading } = useError();
  const redirectPath = localStorage.getItem('redirectPath')?.replace('/compra', '');
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { fichaEspectaculo, loadingFichaEspectaculo, errorFichaEspectaculo } = useSelector(
    (state: RootState) => state.fichaEspectaculo
  );
  const token = getAuthToken();
  const { funciones, loadingFunciones, errorFunciones } = useSelector((state: RootState) => state.funcionesEspectaculo);
  const afiche = fichaEspectaculo?.AficheUrl;
  const funcionesConFechas: FuncionConDate[] = formatearFuncionesConDate(funciones);
  const compraPorOtroCanal = esAdmin && tipoCompra === 2;

  useEffect(() => {
    clearErrorCompra();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(fetchFuncionesEspectaculo(Number(id)));
      dispatch(fetchFichaEspectaculoPorId(Number(id)));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (!loadingFichaEspectaculo) {
      const img = new Image();
      img.src = afiche;
      img.onload = () => setImgSrc(afiche);
      img.onerror = () => setImgSrc(NoImage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [afiche]);

  useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath);
    }
    localStorage.removeItem('redirectPath');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const datosCompraJSON = getCompraConExpiracion('datosCompra');
    const datosCompra = datosCompraJSON && JSON.parse(datosCompraJSON);

    if (datosCompra) {
      if (datosCompra?.CantAsientos) {
        setCantidadAsientos(datosCompra.CantAsientos);
      }

      const funcionId = datosCompra?.FuncionId;
      const funcion = funciones.find(f => f.Id === funcionId);

      if (funcion) {
        const funcionDates: FuncionConDate = {
          ...funcion,
          Fecha: {
            FechaInicio: new Date(funcion.Fecha.FechaInicio),
            FechaFin: new Date(funcion.Fecha.FechaFin),
          },
          CantAsientosDisponibles: funcion.CantAsientosDisponibles,
          Duracion: funcion.Duracion,
        };
        setFuncionSeleccionada(funcionDates);
      }
    }
  }, [funciones]);

  const { register, handleSubmit } = useForm<IFichaEspectaculo>();
  const handleCantidadAsientosChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCantidadAsientos(parseInt(event.target.value, 10));
  };

  const handleChange = (event: SelectChangeEvent) => {
    setTipoCompra(Number(event.target.value));
  };

  const handleOnClick = () => {
    if (compraPorOtroCanal) {
      setOpenModalConfirmacion(true);
    } else {
      handleComprar();
    }
  };

  const handleComprar = async () => {
    clearError();
    setOpenSpinner(true);

    const requestCompra: Compra = {
      CompraId: 0,
      CantAsientos: cantidadAsientos,
      FuncionId: funcionSeleccionada?.Id,
      UsuarioId: compraPorOtroCanal ? -1 : usuario?.Id, // Cuando es compra Admin (por otro canal), el usuario logueado como tal (para la compra) no existe, mando -1
      AdminId: compraPorOtroCanal ? usuario.Id : 0, // Cuando es compra Admin (por otro canal) mando el idUsuario del admin
    };

    storeDatosConExpiracion('datosCompra', JSON.stringify({ ...requestCompra, ...fichaEspectaculo }), 300000); // Guardo los datos de la compra durante 5 minutos, especialmente para el caso de que el usuario tenga que loguearse y volver

    const expiracion = new Date().getTime() + 300000;
    localStorage.setItem('expiracionCompra', JSON.stringify(expiracion));
    let responseCompra;
    if (token) {
      try {
        responseCompra = await compraEntrada(requestCompra, setError, setLoading);
        if (responseCompra) {
          const compraId = responseCompra?.CompraId;
          if (compraPorOtroCanal) {
            localStorage.setItem('idCompra', String(compraId));
            localStorage.setItem(PAYMENT_FLOW_MODE_KEY, 'admin-other-channel');
            localStorage.removeItem('datosCompra');
            localStorage.removeItem('expiracionCompra');
            navigate('/payment/success');
          } else {
            if (compraId) navigate(`/espectaculos/${fichaEspectaculo.Id}/compra/${compraId}`);
            // Borro los datos anteriores, no tienen el descuento
            localStorage.removeItem('datosCompra');

            // Vuelvo a guardar los datos más completos una vez que hizo la compra
            storeDatosConExpiracion(
              'datosCompra',
              JSON.stringify({ ...requestCompra, responseCompra, compraId, ...fichaEspectaculo }),
              300000
            );
          }
        }
      } catch (error) {
        console.error('Error de compra:', error);
        navigate('/payment/failure');
      }
    } else {
      localStorage.setItem('redirectPath', `/espectaculos/${id}/compra`);

      dispatch(loginFailure('Necesitás estar logueado para efectuar una compra'));
      navigate('/login');
    }
  };

  const hayFunciones = funciones.length > 0; // Esto contempla casos en que el espectáculo ya no está vigente
  const hayFotos = fichaEspectaculo.Fotos.length > 0;

  const onSubmit = (_data: IFichaEspectaculo) => {};

  const handleDateSelect = (funcion: FuncionConDate) => {
    setFuncionSeleccionada(funcion);
  };

  const fechaSeleccionada = funcionSeleccionada?.Fecha.FechaInicio || new Date();
  const fechaHora = formatoFechaHora(fechaSeleccionada);
  const funcionesAgotadas = hayFunciones && funciones.every(f => f.CantAsientosDisponibles === 0);
  const funcionAgotada = funcionSeleccionada && funcionSeleccionada.CantAsientosDisponibles === 0;
  const menosDe10Asientos = funcionSeleccionada && funcionSeleccionada.CantAsientosDisponibles < 10;

  return (
    <PageContainer marginBottom={hayFotos ? false : true} sx={{ paddingBottom: '50px', minHeight: alturaContainer }}>
      {loadingFichaEspectaculo || loadingFunciones ? (
        <Spinner />
      ) : !errorFichaEspectaculo && !errorFunciones ? (
        // CONTAINER DE TODA LA FICHA
        <Box maxWidth={anchoMaxForms} mx="auto" px={{ xs: 0, lg: 10 }}>
          {hayFunciones && <PasosDeCompra paso={0} />}
          <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={openSpinner}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <ModalConfirmacion
            open={openModalConfirmacion}
            onClose={() => setOpenModalConfirmacion(false)}
            onConfirm={handleComprar}
            titulo="CONFIRMAR COMPRA"
            mensaje="¿Deseás confirmar esta compra realizada por otro medio?"
            variante="warning"
            colorBoton={theme.palette.primary.main}
          />
          <Box component="form" onSubmit={onSubmit && handleSubmit(onSubmit)} textAlign="left">
            <Button fullWidth={false} sx={{ mb: 2 }} onClick={() => navigate(-1)} startIcon={<ChevronLeft />}>
              Volver
            </Button>
            <FormControl fullWidth>
              {hayFunciones ? (
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.5,
                      fontSize: '1.2rem',
                      color: theme.palette.primary.main,
                      pb: 0,
                    }}
                  >
                    1. Seleccioná una función
                  </Typography>
                  <CarouselFunciones
                    funcionId={funcionSeleccionada?.Id}
                    funcionesConFechas={funcionesConFechas}
                    onDateSelect={handleDateSelect}
                    {...register('Funciones')}
                  />
                </Box>
              ) : (
                <Box pb={4} display="flex" flexDirection="column" justifyContent="center" textAlign="left">
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '1.2rem',
                      color: !funcionesAgotadas ? theme.palette.primary.main : 'red',
                    }}
                  >
                    {!funcionesAgotadas ? 'Sin próximas funciones' : '¡Funciones agotadas!'}
                  </Typography>
                </Box>
              )}
              <Box
                display="flex"
                flexDirection={{ xs: 'column', lg: 'row' }}
                justifyContent="space-around"
                gap={{ xs: 3, xl: 6 }}
                width="100%"
                flex="1 1 auto"
                textAlign="center"
              >
                <CaratulaObra afiche={imgSrc} nombre={fichaEspectaculo.Nombre} colorTitulo="white" color="black" />

                <Box
                  flex="1"
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap={{ xs: 3, xl: 6 }}
                  justifyContent="space-evenly"
                >
                  <InformacionEspectaculo esp={fichaEspectaculo} />
                  {hayFunciones && (
                    <>
                      <Box
                        textAlign="left"
                        flex="1"
                        maxHeight="500px"
                        boxSizing="border-box"
                        sx={{ overflowY: 'auto' }}
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                      >
                        <Box height="unset" width={{ xs: '90%', xl: 'unset' }}>
                          {menosDe10Asientos && (
                            <Typography
                              pb={2}
                              sx={{
                                fontWeight: 600,
                                lineHeight: 1.5,
                                fontSize: '1.2rem',
                              }}
                              color="error"
                            >
                              {funcionAgotada ? '¡Función agotada!' : '¡Quedan menos de 10 lugares!'}
                            </Typography>
                          )}
                          {!funcionAgotada && (
                            <Typography
                              pb={2}
                              sx={{
                                fontWeight: 600,
                                lineHeight: 1.5,
                                fontSize: '1.2rem',
                                color: funcionSeleccionada ? theme.palette.primary.main : 'lightgray',
                              }}
                            >
                              2. Seleccioná tus asientos
                            </Typography>
                          )}
                          {funcionSeleccionada && (
                            <Typography
                              fontSize="1.1rem"
                              variant="h6"
                              fontWeight={600}
                              component="div"
                              color={theme.palette.primary.dark}
                              pb={2}
                            >
                              {fechaHora}
                            </Typography>
                          )}
                          <Typography
                            variant="body1"
                            color={funcionSeleccionada ? theme.palette.primary.main : 'lightgray'}
                          >
                            Precio de la entrada: <b>${fichaEspectaculo.Precio}</b>
                          </Typography>
                          <TextField
                            fullWidth
                            margin="normal"
                            label="Cantidad de asientos"
                            type="number"
                            onChange={handleCantidadAsientosChange}
                            inputProps={{ min: 1, max: fichaEspectaculo.AforoDisponible }}
                            variant="outlined"
                            disabled={!funcionSeleccionada || !!funcionAgotada}
                            size="small"
                            defaultValue={cantidadAsientos ? cantidadAsientos : 0}
                          />

                          {esAdmin && (
                            <>
                              <InputLabel shrink={true} size="small" sx={{ position: 'relative', mb: '-25px', mt: 1 }}>
                                Tipo de compra
                              </InputLabel>
                              <Select
                                fullWidth
                                label="Tipo de compra"
                                name="TipoCompra"
                                defaultValue={'1'}
                                onChange={handleChange}
                                disabled={!funcionSeleccionada || !!funcionAgotada}
                                size="small"
                                required
                              >
                                <MenuItem value={1}>Como usuario</MenuItem>
                                <MenuItem value={2}>Para otro usuario</MenuItem>
                              </Select>
                            </>
                          )}
                        </Box>
                        {funcionSeleccionada && !!cantidadAsientos && (
                          <Box
                            lineHeight={2}
                            padding="15px"
                            my={2}
                            sx={{ backgroundColor: theme.palette.secondary.light }}
                            borderRadius={4}
                          >
                            <Typography variant="body1" sx={{ mb: 2 }} color={theme.palette.primary.dark}>
                              Resumen de tu selección
                            </Typography>
                            <Typography variant="h6">{fichaEspectaculo.Nombre}</Typography>

                            <Typography variant="body2">{fechaHora?.toUpperCase()}</Typography>
                            <Box display="flex" justifyContent="space-between" mt={2}>
                              <Typography variant="h6">
                                <b>Total:</b>
                              </Typography>
                              <Typography variant="h6">${cantidadAsientos * fichaEspectaculo.Precio}</Typography>
                            </Box>
                          </Box>
                        )}
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={handleOnClick}
                          disabled={!funcionSeleccionada || cantidadAsientos < 1 || !!funcionAgotada}
                          sx={{
                            mt: 1,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark,
                              color: 'white',
                            },
                          }}
                        >
                          Comprar
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </FormControl>
          </Box>
          {hayFotos && <GaleriaEspectaculo espectaculo={fichaEspectaculo} />}
        </Box>
      ) : (
        <RespuestaServidor
          titulo="Error del servidor"
          texto={`No se pudo cargar el espectáculo. Intentalo de nuevo más tarde o contactate con un administrador del sitio.`}
          icono={ErrorOutlineIcon}
          textoBoton="Reintentar"
          colorIcono="error"
          paginaANavegar={0}
        />
      )}
    </PageContainer>
  );
};

export default FichaEspectaculo;
